import {AnimatePresence} from 'framer-motion';
import {Box, CSSDimension, HStack, Integer, ValueByDevice} from 'platform/foundation';

import {FocusEvent, KeyboardEvent, useId, useRef} from 'react';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {SegmentItem, SegmentItemBaseProps} from './SegmentItem';

export interface SegmentProps<T extends string = string> extends FormControlProps<T>, TestIdProps {
  options?: SegmentItemBaseProps<T>[];
  minWidth?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer>;
}

export function Segment<T extends string = string>(props: SegmentProps<T>) {
  const id = useId();
  const count = props.options?.length ?? 0;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFocused, focusInput, unfocusInput] = useBoolean();

  const enabledOptions = props.options?.filter((option) => !option.isDisabled) ?? [];
  const currentIndex = enabledOptions.findIndex((option) => option.value === props.value);

  const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
    focusInput();
    // Note: props.onFocus expects HTMLInputElement event, but we have HTMLDivElement
    // We'll handle focus internally for now
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    unfocusInput();
    // Note: props.onBlur expects HTMLInputElement event, but we have HTMLDivElement
    // We'll handle blur internally for now
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (enabledOptions.length === 0) {
      return;
    }

    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        newIndex = currentIndex < enabledOptions.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : enabledOptions.length - 1;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = enabledOptions.length - 1;
        break;
      default:
        return;
    }

    if (newIndex !== currentIndex && enabledOptions[newIndex]) {
      props.onChange?.(enabledOptions[newIndex].value);
    }
  };

  return (
    <AnimatePresence mode="wait">
      <div
        ref={containerRef}
        role="radiogroup"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-label={props.label || 'Segment control'}
        aria-required={props.isRequired}
        aria-invalid={props.isInvalid || !!props.errorMessage}
        style={{outline: 'none'}}
      >
        <Box
          padding={1}
          backgroundColor="palettes.neutral.20.100"
          borderRadius="small"
          data-itemcount={count}
          data-testid={suffixTestId('segmentWrapper', props)}
        >
          <HStack spacing={1} data-testid={suffixTestId('segmentWrapper', props)}>
            {props.options?.map((item) => (
              <SegmentItem<T>
                {...item}
                key={item.value}
                id={id}
                onClick={props.onChange}
                isSelected={item.value === props.value}
                isFocused={isFocused && item.value === props.value}
                minWidth={props.minWidth}
                data-testid={suffixTestId(`segmentItem-${item.value}`, props)}
              />
            ))}
          </HStack>
        </Box>
      </div>
    </AnimatePresence>
  );
}
